import React, { useState, useEffect } from 'react';
import PolicyAllowLists from './PolicyAllowLists.jsx';
import rtoService from '../../services/rtoService';
import toastService from '../../services/toastService';
import { Card, CardBody, CardHeader } from './../../components/card/card.jsx';

// Simple IPv4 CIDR validator (e.g., 10.0.0.0/8)
function isValidIPv4CIDR(value) {
  if (typeof value !== 'string') return false;
  const m = value.trim().match(/^([0-9]{1,3})\.([0-9]{1,3})\.([0-9]{1,3})\.([0-9]{1,3})\/(\d{1,2})$/);
  if (!m) return false;
  const octets = [m[1], m[2], m[3], m[4]].map(n => Number(n));
  const prefix = Number(m[5]);
  if (prefix < 0 || prefix > 32) return false;
  return octets.every(o => o >= 0 && o <= 255);
}

const ListEditor = ({ label, items, setItems, placeholder, onValidate }) => {
  const [input, setInput] = useState('');

  const addItem = () => {
    const v = input.trim();
    if (!v) return;
    if (items.includes(v)) {
      toastService.info(`${label} already includes: ${v}`);
      return;
    }
    if (typeof onValidate === 'function') {
      const res = onValidate(v);
      if (res !== true) {
        const msg = typeof res === 'string' ? res : `Invalid value for ${label}: ${v}`;
        toastService.error(msg);
        return;
      }
    }
    setItems([...items, v]);
    setInput('');
  };

  const removeItem = (idx) => {
    const next = items.slice();
    next.splice(idx, 1);
    setItems(next);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addItem();
    }
  };

  return (
    <div className="mb-4">
      <label className="form-label fw-semibold">{label}</label>
      <div className="d-flex gap-2 mb-2">
        <input
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <button className="btn btn-primary" onClick={addItem}>Add</button>
      </div>
      {items.length === 0 ? (
        <div className="text-muted small">No entries yet.</div>
      ) : (
        <ul className="list-group">
          {items.map((v, idx) => (
            <li key={`${label}-${idx}`} className="list-group-item d-flex justify-content-between align-items-center">
              <code>{v}</code>
              <button className="btn btn-sm btn-outline-danger" onClick={() => removeItem(idx)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// New: Editor supporting pattern + reason, and showing created_at if present
const EntryEditor = ({ label, entries, setEntries, placeholder, onValidate }) => {
  const [pattern, setPattern] = useState('');
  const [reason, setReason] = useState('');

  const addEntry = () => {
    const p = (pattern || '').trim();
    const r = (reason || '').trim();
    if (!p) return;
    if (typeof onValidate === 'function') {
      const res = onValidate(p);
      if (res !== true) {
        const msg = typeof res === 'string' ? res : `Invalid value for ${label}: ${p}`;
        toastService.error(msg);
        return;
      }
    }
    if (entries.some(e => e.pattern === p)) {
      toastService.info(`${label} already includes: ${p}`);
      return;
    }
    setEntries([...entries, { pattern: p, reason: r }]);
    setPattern('');
    setReason('');
  };

  const removeEntry = (idx) => {
    const next = entries.slice();
    next.splice(idx, 1);
    setEntries(next);
  };

  const updateReason = (idx, newReason) => {
    const next = entries.slice();
    next[idx] = { ...next[idx], reason: newReason };
    setEntries(next);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEntry();
    }
  };

  return (
    <div className="mb-4">
      <label className="form-label fw-semibold">{label}</label>
      <div className="row g-2 mb-2">
        <div className="col-5">
          <input
            type="text"
            className="form-control"
            placeholder={placeholder}
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            onKeyDown={onKeyDown}
          />
        </div>
        <div className="col-5">
          <input
            type="text"
            className="form-control"
            placeholder="Optional reason (e.g., Suspicious activity)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={onKeyDown}
          />
        </div>
        <div className="col-2 d-grid">
          <button className="btn btn-primary" onClick={addEntry}>Add</button>
        </div>
      </div>
      {entries.length === 0 ? (
        <div className="text-muted small">No entries yet.</div>
      ) : (
        <ul className="list-group">
          {entries.map((e, idx) => (
            <li key={`${label}-${idx}`} className="list-group-item">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                <div className="d-flex align-items-center gap-2">
                  <code>{e.pattern}</code>
                </div>
                <div className="flex-grow-1">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Reason (optional)"
                    value={e.reason || ''}
                    onChange={(ev) => updateReason(idx, ev.target.value)}
                  />
                </div>
                <div className="text-muted small" title={e.created_at || ''}>
                  {e.created_at ? new Date(e.created_at).toLocaleString() : ''}
                </div>
                <div>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => removeEntry(idx)}>
                    Remove
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const BlockLists = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Switched to entry objects with {pattern, reason, created_at}
  const [blockedCidrsEntries, setBlockedCidrsEntries] = useState([]);
  const [blockedHostsEntries, setBlockedHostsEntries] = useState([]);
  const [error, setError] = useState('');
  const [available, setAvailable] = useState(true);

  const fetchLists = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await rtoService.getPolicyBlockLists();
      const cidrDetails = Array.isArray(data.blocked_cidrs_details) ? data.blocked_cidrs_details : [];
      const hostDetails = Array.isArray(data.blocked_hosts_details) ? data.blocked_hosts_details : [];
      // Fallback if only arrays provided
      const cidrFallback = (Array.isArray(data.blocked_cidrs) ? data.blocked_cidrs : []).map((p) => ({ pattern: p, reason: '' }));
      const hostFallback = (Array.isArray(data.blocked_hosts) ? data.blocked_hosts : []).map((p) => ({ pattern: p, reason: '' }));
      setBlockedCidrsEntries(cidrDetails.length ? cidrDetails : cidrFallback);
      setBlockedHostsEntries(hostDetails.length ? hostDetails : hostFallback);
      setAvailable(true);
    } catch (e) {
      // If server endpoint doesn't exist yet, mark as unavailable but don't spam errors
      if (e?.response?.status === 404) {
        setAvailable(false);
        setError('Block-lists endpoint is not available on the server yet.');
      } else {
        setError(e.message || 'Failed to load block-lists');
        toastService.error(e.message || 'Failed to load block-lists');
      }
    } finally {
      setLoading(false);
    }
  };

  const parseServerError = (e) => {
    if (!e) return 'Failed to update block-lists';
    const status = e?.response?.status ?? e.status;
    if (status === 404) return 'Block-lists endpoint is not available on the server yet.';
    if (status === 422) {
      const d = e.response?.data?.detail ?? e.data?.detail;
      if (typeof d === 'string') return d;
      if (Array.isArray(d)) {
        const msgs = d.map((x) => x.msg || x.message || JSON.stringify(x));
        return msgs.join('; ');
      }
    }
    return e.message || 'Failed to update block-lists';
  };

  const saveLists = async () => {
    setSaving(true);
    setError('');

    // Client-side validation for CIDRs before sending
    const invalidCidrs = blockedCidrsEntries
      .map((e) => e.pattern)
      .filter((c) => !isValidIPv4CIDR(c));
    if (invalidCidrs.length > 0) {
      const msg = `Invalid CIDR entr${invalidCidrs.length > 1 ? 'ies' : 'y'}: ${invalidCidrs.join(', ')}`;
      setSaving(false);
      setError(msg);
      toastService.error(msg);
      return;
    }

    try {
      const payloadCidrs = blockedCidrsEntries.map(({ pattern, reason }) => ({ pattern, reason }));
      const payloadHosts = blockedHostsEntries.map(({ pattern, reason }) => ({ pattern, reason }));
      const data = await rtoService.updatePolicyBlockLists(payloadCidrs, payloadHosts);
      // Normalize response
      const cidrDetails = Array.isArray(data.blocked_cidrs_details) ? data.blocked_cidrs_details : [];
      const hostDetails = Array.isArray(data.blocked_hosts_details) ? data.blocked_hosts_details : [];
      const cidrFallback = (Array.isArray(data.blocked_cidrs) ? data.blocked_cidrs : []).map((p) => ({ pattern: p, reason: '' }));
      const hostFallback = (Array.isArray(data.blocked_hosts) ? data.blocked_hosts : []).map((p) => ({ pattern: p, reason: '' }));
      setBlockedCidrsEntries(cidrDetails.length ? cidrDetails : cidrFallback);
      setBlockedHostsEntries(hostDetails.length ? hostDetails : hostFallback);
      toastService.success('Block-lists updated');
    } catch (e) {
      const msg = parseServerError(e);
      setError(msg);
      toastService.error(msg);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="h4 mb-0">Policy Block-Lists</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={fetchLists} disabled={loading}>
            Refresh
          </button>
          <button className="btn btn-primary" onClick={saveLists} disabled={saving || loading || !available}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {!available && (
        <div className="alert alert-warning" role="alert">
          Block-lists endpoints are not yet available on the server. This UI is ready and will work once the backend is implemented.
        </div>
      )}

      {error && (
        <div className="alert alert-danger" role="alert">{error}</div>
      )}

      <Card>
        <CardHeader>
          <div className="fw-semibold">Runtime Block-Lists for RT Orchestrator Policy</div>
          <div className="text-muted small">These lists prevent targets such as hostnames, IPs, and URIs from being used.</div>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="d-flex align-items-center">
              <div className="spinner-border me-2" role="status" /> Loading…
            </div>
          ) : (
            <>
              <EntryEditor
                label="Blocked CIDRs"
                entries={blockedCidrsEntries}
                setEntries={setBlockedCidrsEntries}
                placeholder="e.g. 192.168.0.0/16"
                onValidate={(v) => (isValidIPv4CIDR(v) ? true : 'Please enter a valid IPv4 CIDR, e.g. 192.168.1.0/24')}
              />

              <EntryEditor
                label="Blocked Hosts"
                entries={blockedHostsEntries}
                setEntries={setBlockedHostsEntries}
                placeholder="e.g. corp.example.com or 10.0.0.1"
              />

              <div className="text-muted small mt-3">
                Notes:
                <ul className="mt-2">
                  <li>Changes take effect immediately for new policy evaluations.</li>
                  <li>Invalid CIDR entries will be rejected by the server.</li>
                </ul>
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

const AdminPolicies = () => {
  const [activeTab, setActiveTab] = useState('allow');

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h2 mb-0">Policies</h1>
      </div>

      <ul className="nav nav-tabs">
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'allow' ? 'active' : ''}`} onClick={() => setActiveTab('allow')}>
            Allow-Lists
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'block' ? 'active' : ''}`} onClick={() => setActiveTab('block')}>
            Block-Lists
          </button>
        </li>
      </ul>

      <div className="tab-content">
        <div className={`tab-pane fade ${activeTab === 'allow' ? 'show active' : ''}`}>
          {/* Reuse existing page as-is for now */}
          <div className="mt-3">
            <PolicyAllowLists />
          </div>
        </div>
        <div className={`tab-pane fade ${activeTab === 'block' ? 'show active' : ''}`}>
          <BlockLists />
        </div>
      </div>
    </div>
  );
};

export default AdminPolicies;